import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useTranslation } from '@/utils/i18n';
import { joinViaInviteCode } from '@/api/groups';
import { normalizeInviteCode } from '@/utils/inviteCode';
import { GROUPS_QUERY_KEY } from '@/hooks/queries/groups';
import { toast } from '@/stores/toast';
import BottomSheet from './BottomSheet';
import { PrimaryButton } from '@/components/Buttons';

/**
 * "Rejoindre un cercle" sheet: paste an invite code → sends a join request the
 * circle's admin must approve.
 */
export default function JoinCircleSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    const trimmed = normalizeInviteCode(code);
    if (!trimmed) return;
    setLoading(true);
    try {
      const { pending } = await joinViaInviteCode(trimmed);
      await qc.invalidateQueries({ queryKey: GROUPS_QUERY_KEY });
      toast.success(pending ? t('groups.join.requestSent') : t('groups.join.success'));
      setCode('');
      onClose();
    } catch {
      toast.error(t('groups.join.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <BottomSheet
      open={open}
      title={t('groups.join.title')}
      onClose={() => {
        setCode('');
        onClose();
      }}
      testid="join-circle-sheet"
    >
      <div className="tw:flex tw:flex-col tw:gap-4 tw:px-1 tw:pb-2">
        <p className="tw:text-[13px] tw:text-grey-2">{t('groups.join.codeHint')}</p>
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder={t('groups.join.codePlaceholder')}
          data-testid="input-join-code"
          maxLength={12}
          className="tw:h-12 tw:w-full tw:rounded-2xl tw:border tw:border-white/10 tw:bg-dark-primary tw:px-4 tw:text-center tw:text-[18px] tw:font-semibold tw:tracking-[0.2em] tw:text-white tw:placeholder:text-grey-4 tw:placeholder:tracking-normal tw:focus:outline-none"
        />
        <PrimaryButton
          label={t('groups.join.submit')}
          onClick={submit}
          disabled={!code.trim() || loading}
          loading={loading}
          data-testid="btn-join-submit"
        />
      </div>
    </BottomSheet>
  );
}
